import { useState, useEffect } from 'react';
import { subscribeToWatchlist } from '../services/watchlistService';
import { getContentByIds, type ContentItem } from '../services/contentService';

export interface WatchlistItem {
  contentId: string;
  title?: string | null;
  content?: ContentItem;
  addedDate?: string;
  status?: string;
  progress?: number;
}

export interface WatchlistStream {
  items: WatchlistItem[];
  loading: boolean;
  error: string | null;
}

export function useWatchlistStream(userId: string | null): WatchlistStream {
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setItems([]);
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time subscription
    const unsubscribe = subscribeToWatchlist(userId, async (watchlist) => {
      try {
        const contentIds = watchlist.map(item => item.contentId);
        const contents = await getContentByIds(contentIds);

        const watchlistWithContent = watchlist.map(item => ({
          ...item,
          content: contents.find((c: ContentItem) => c.id === item.contentId),
          addedDate: new Date().toISOString(), // TODO: Get actual added date from Firebase
          status: 'plan-to-watch', // TODO: Get actual status from Firebase
          progress: 0 // TODO: Get actual progress from Firebase
        }));
        setItems(watchlistWithContent);
        setError(null);
      } catch (err) {
        console.error('Error loading watchlist content:', err);
        // Don't set error for connection issues, just log them
        if (err instanceof Error && !err.message.includes('QUIC_PROTOCOL_ERROR')) {
          setError('Failed to load watchlist content');
        }
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [userId]);

  return { items, loading, error };
}

